import {
	BarChart3,
	Bot,
	Files,
	LogOut,
	PanelLeftClose,
	PanelLeftOpen,
	ScrollText,
	Search,
	Sparkles,
	Tags,
} from "lucide-react"
import { NavLink, useLocation } from "react-router-dom"
import { useSession, useSessionRole } from "@/entities/session"
import logo from "@/shared/assets/kmk-logo.png"
import { openCommandPalette } from "@/shared/lib/command-palette"
import { cn } from "@/shared/lib/utils"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { SlidingPill } from "@/shared/ui/sliding-pill"
import { LinkTelegramButton } from "./link-telegram-button"

/** Материал, его редактор и создание живут под разделом «Контент». */
const CONTENT_PATHS = /^\/admin(\/(posts|edit|create|publish)(\/|$).*)?$/

const sections = [
	{ to: "/admin", label: "Контент", icon: Files, end: true },
	{ to: "/admin/analytics", label: "Аналитика", icon: BarChart3, end: false },
	{ to: "/admin/bots", label: "Боты", icon: Bot, end: false },
	{ to: "/admin/taxonomy", label: "Классификаторы", icon: Tags, end: false },
	{ to: "/admin/mcp", label: "MCP", icon: Sparkles, end: false },
	{ to: "/admin/audit", label: "Журнал", icon: ScrollText, end: false, adminOnly: true },
]

const isSection = (pathname: string, to: string, end: boolean) =>
	end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`)

/**
 * Свёртка меняет ширину всей колонки и сдвигает контент, поэтому ей нужна симметричная
 * кривая: `--ease-out-soft` отдаёт почти всё движение в первые 100 мс и читается рывком.
 */
export const NAV_EASE = "ease-[cubic-bezier(0.65,0,0.35,1)]"
export const NAV_DURATION = "duration-[400ms]"

/**
 * Подписи гаснут быстрее, чем сужается колонка, иначе край режет ещё видимый текст.
 * При разворачивании наоборот: появляются, когда места уже хватает.
 */
const labelFade = (collapsed: boolean) =>
	cn(
		NAV_EASE,
		collapsed
			? "[transition-duration:220ms,400ms]"
			: "[transition-delay:140ms,0ms] [transition-duration:260ms,400ms]",
	)

/** Подпись пункта: в свёрнутой колонке гаснет и сжимается, иконка остаётся на месте. */
function ItemLabel({ collapsed, children }: { collapsed: boolean; children: string }) {
	return (
		<span
			className={cn(
				"truncate transition-[opacity,max-width]",
				labelFade(collapsed),
				collapsed ? "lg:max-w-0 lg:opacity-0" : "lg:max-w-40 lg:opacity-100",
			)}
		>
			{children}
		</span>
	)
}

/**
 * Разделы бэкофиса. От 1024px боковая колонка, её можно свернуть до иконок (⌘\);
 * уже одна строка, чтобы не отнимать высоту у экрана.
 */
export function AdminNavigation({
	collapsed,
	onToggle,
}: {
	collapsed: boolean
	onToggle: () => void
}) {
	const { user, logout } = useSession()
	const { confirm } = useConfirm()
	const { isAdmin } = useSessionRole()
	const { pathname } = useLocation()
	const itemClass =
		"flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl px-3 text-sm transition-colors lg:min-h-12 lg:w-full lg:justify-start lg:gap-3 lg:px-3.5"

	return (
		<aside
			className={cn(
				"sticky top-0 z-30 border-b border-border bg-background/95 px-2 py-2 backdrop-blur lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-[var(--nav-width)] lg:flex-col lg:border-r lg:border-b-0 lg:bg-background lg:pt-10 lg:backdrop-blur-none",
				`transition-[width,padding] ${NAV_DURATION} ${NAV_EASE}`,
				"motion-safe:animate-[nav-enter_480ms_var(--ease-out-soft)_both]",
				collapsed ? "lg:px-3" : "lg:px-4",
			)}
		>
			<div
				className={cn(
					"mb-4 hidden h-8 items-center whitespace-nowrap transition-[padding] lg:flex",
					NAV_DURATION,
					NAV_EASE,
					// Без justify-center: он переключается мгновенно и сдёргивает логотип в центр
					// ещё широкой колонки. Центр свёрнутой колонки достигается одним отступом.
					collapsed ? "px-2" : "px-3.5",
				)}
			>
				<img src={logo} alt="КМК" className="size-8 shrink-0 rounded-lg shadow-lg shadow-primary/30" />
				<span
					className={cn(
						"overflow-hidden text-lg font-semibold tracking-tight transition-[max-width,opacity]",
						labelFade(collapsed),
						collapsed ? "max-w-0 opacity-0" : "max-w-40 opacity-100",
					)}
				>
					<span className="pl-2.5">Бэкофис</span>
				</span>
			</div>
			<div className="flex items-center gap-1 lg:flex-1 lg:flex-col lg:items-stretch">
				{/* Узкий экран: раздел не ужимается до «Класси…», а ряд при нехватке
				    места прокручивается. */}
				<nav
					aria-label="Разделы"
					className="hide-scrollbar relative flex min-w-0 flex-1 gap-1 overflow-x-auto lg:flex-none lg:flex-col lg:overflow-visible"
				>
					<SlidingPill className="rounded-xl bg-secondary shadow-none" />
					{sections
						.filter((section) => isAdmin || !("adminOnly" in section && section.adminOnly))
						.map(({ to, label, icon: Icon, end }) => {
							const active = to === "/admin" ? CONTENT_PATHS.test(pathname) : isSection(pathname, to, end)
							return (
								<NavLink
									key={to}
									to={to}
									end={end}
									title={collapsed ? label : undefined}
									data-active={active}
									className={cn(
										itemClass,
										"relative",
										active
											? "text-foreground"
											: "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
									)}
								>
									<Icon className="relative hidden size-4 shrink-0 sm:block" />
									<span className="relative flex min-w-0">
										<ItemLabel collapsed={collapsed}>{label}</ItemLabel>
									</span>
								</NavLink>
							)
						})}
				</nav>

				<div className="lg:mt-auto lg:space-y-1 lg:pb-6">
					{user?.email && (
						<p
							className={cn(
								"hidden truncate px-3.5 pb-2 text-xs text-muted-foreground transition-opacity ease-[cubic-bezier(0.65,0,0.35,1)] lg:block",
								collapsed ? "duration-200 lg:opacity-0" : "delay-150 duration-300",
							)}
						>
							{user.email}
						</p>
					)}
					<button
						type="button"
						onClick={openCommandPalette}
						aria-label="Поиск"
						title="Поиск: люди, метки, рассылки, материалы (⌘K)"
						className={cn(itemClass, "text-muted-foreground hover:bg-secondary/50 hover:text-foreground")}
					>
						<Search className="size-4 shrink-0" />
						<span className="hidden lg:inline-flex">
							<ItemLabel collapsed={collapsed}>Поиск ⌘K</ItemLabel>
						</span>
					</button>
					<LinkTelegramButton
						className={itemClass}
						label={
							<span className="hidden lg:inline-flex">
								<ItemLabel collapsed={collapsed}>Привязать Telegram</ItemLabel>
							</span>
						}
					/>
					<button
						type="button"
						onClick={onToggle}
						aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
						title={`${collapsed ? "Развернуть" : "Свернуть"} меню (⌘\\)`}
						className={cn(itemClass, "hidden text-muted-foreground hover:bg-secondary/50 hover:text-foreground lg:flex")}
					>
						{collapsed ? (
							<PanelLeftOpen className="size-4 shrink-0" />
						) : (
							<PanelLeftClose className="size-4 shrink-0" />
						)}
						<ItemLabel collapsed={collapsed}>Свернуть</ItemLabel>
					</button>
					<button
						type="button"
						onClick={() =>
							confirm({
								title: "Выйти из аккаунта?",
								message: "Чтобы вернуться в бэкофис, придётся войти заново.",
								confirmText: "Выйти",
								onConfirm: logout,
							})
						}
						aria-label="Выйти"
						title="Выйти"
						className={cn(itemClass, "text-muted-foreground hover:bg-secondary/50 hover:text-foreground")}
					>
						<LogOut className="size-4 shrink-0" />
						<span className="hidden lg:inline-flex">
							<ItemLabel collapsed={collapsed}>Выйти</ItemLabel>
						</span>
					</button>
				</div>
			</div>
		</aside>
	)
}
